/**
 * ReviewsSection.jsx
 *
 * Homepage section displaying player reviews
 * - Fetches reviews from API
 * - Shows rating summary
 * - Displays carousel of top reviews
 * - SEO-friendly with schema.org microdata
 *
 * No props required - self-contained component
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ReviewCarousel from './ReviewCarousel';

const ReviewsSection = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Weighted shuffle algorithm - higher rated reviews appear more frequently
   * 5 stars: weight 5 (appears 5x in pool)
   * 4 stars: weight 4 (appears 4x in pool)
   * 3 stars: weight 3 (appears 3x in pool)
   * 2 stars: weight 2 (appears 2x in pool)
   * 1 star: weight 1 (appears 1x in pool)
   */
  const weightedShuffle = (reviewsList) => {
    const weightedPool = [];

    // Add each review to pool based on rating weight
    reviewsList.forEach(review => {
      const weight = review.rating; // Use rating as weight (1-5)
      for (let i = 0; i < weight; i++) {
        weightedPool.push(review);
      }
    });

    // Shuffle the weighted pool
    for (let i = weightedPool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [weightedPool[i], weightedPool[j]] = [weightedPool[j], weightedPool[i]];
    }

    // Remove duplicates while preserving weighted order
    const seen = new Set();
    const uniqueReviews = [];

    for (const review of weightedPool) {
      if (!seen.has(review.id)) {
        seen.add(review.id);
        uniqueReviews.push(review);
      }
    }

    return uniqueReviews;
  };

  /**
   * Fetch reviews and stats on component mount
   */
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001';

        // Fetch ALL reviews (1-5 stars) - weighted display handles frequency
        const reviewsResponse = await fetch(`${apiUrl}/api/reviews?limit=50&minRating=1`);
        const reviewsData = await reviewsResponse.json();

        if (reviewsData.success && reviewsData.reviews.length > 0) {
          // Apply weighted shuffle - high-rated reviews appear more often
          const weightedReviews = weightedShuffle(reviewsData.reviews);
          setReviews(weightedReviews.slice(0, 10)); // Take top 10 after weighting
        }

        // Fetch statistics for summary
        const statsResponse = await fetch(`${apiUrl}/api/reviews/stats`);
        const statsData = await statsResponse.json();

        if (statsData.success) {
          setStats(statsData);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  /**
   * Render star rating display
   */
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <span key={i} className="rating-star filled">
            ★
          </span>
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <span key={i} className="rating-star half">
            ★
          </span>
        );
      } else {
        stars.push(
          <span key={i} className="rating-star empty">
            ☆
          </span>
        );
      }
    }

    return stars;
  };

  // Don't render if no reviews yet
  if (loading) {
    return (
      <section className="reviews-section loading">
        <div className="reviews-container">
          <p>{t('reviews.loading')}</p>
        </div>
      </section>
    );
  }

  // Don't render if error or no reviews
  if (error || !reviews || reviews.length === 0) {
    return null; // Graceful degradation - hide section
  }

  return (
    <section className="reviews-section" itemScope itemType="https://schema.org/Product">
      <div className="reviews-container">
        {/* Section Title */}
        <h2 className="reviews-section-title">
          {t('reviews.sectionTitle')}
        </h2>

        {/* Rating Summary with Schema.org markup */}
        {stats && stats.totalReviews > 0 && (
          <div
            className="reviews-rating-summary"
            itemProp="aggregateRating"
            itemScope
            itemType="https://schema.org/AggregateRating"
          >
            <div className="rating-summary-stars">
              {renderStars(stats.averageRating)}
            </div>
            <div className="rating-summary-text">
              <span className="rating-value" itemProp="ratingValue">
                {stats.averageRating}
              </span>
              <meta itemProp="bestRating" content="5" />
              <meta itemProp="worstRating" content="1" />
              {' '}
              {t('reviews.stars')} {t('reviews.basedOn')}{' '}
              <span className="rating-count" itemProp="reviewCount">
                {stats.totalReviews}
              </span>
              {' '}
              {stats.totalReviews === 1 ? t('reviews.review') : t('reviews.reviewCount')}
            </div>
          </div>
        )}

        {/* Reviews Carousel */}
        <div className="reviews-carousel-wrapper">
          <ReviewCarousel reviews={reviews} />
        </div>

        {/* Informational Text - Encourages Playing */}
        <div className="reviews-play-to-review">
          <p>{t('reviews.playToReview')}</p>
        </div>
      </div>

      {/* Hidden meta for schema.org */}
      <meta itemProp="name" content="Qwixx Online" />
      <meta itemProp="description" content={t('seo.description')} />
    </section>
  );
};

export default ReviewsSection;
